import { FC, PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { get } from 'lodash';

import { BDTO, useGetPlanBenefitsInfinite } from '@helpful/network';

import { BenefitsProviderContext } from './BenefitsProvider.context';
import { useBenefitsProvider } from './BenefitsProvider.hooks';

type BenefitsProviderProps = PropsWithChildren<{}>;

export const BenefitsProvider: FC<BenefitsProviderProps> = ({ children }) => {
  const { slug } = useBenefitsProvider();

  const [filters, setFilters] = useState<BDTO.GetPlanBenefitsParams>({});

  const {
    refetch,
    data: all,
    fetchNextPage,
    hasNextPage,
    isLoading,
  } = useGetPlanBenefitsInfinite(slug, filters, {
    query: {
      getNextPageParam: ({ total }, all) => {
        const next = all.reduce((res, { data }) => res + data.length, 0);
        return total > next ? next : undefined;
      },
    },
  });

  const total = get(all, ['pages', 0, 'total'], 0);

  const meta = get(all, ['pages', 0, 'meta']);

  const data = useMemo(() => {
    return get(all, ['pages'], []).flatMap(({ data }) => data);
  }, [all]);

  const filter = useCallback((value: Partial<BDTO.GetPlanBenefitsParams>) => {
    setFilters((prev) => ({ ...prev, ...value }));
  }, []);

  const contextValue = useMemo(
    () => ({
      meta,
      isLoading,
      data,
      fetchNextPage,
      hasNextPage,
      total,
      refetch,
      filter,
      filters,
    }),
    [
      isLoading,
      data,
      fetchNextPage,
      hasNextPage,
      total,
      refetch,
      filter,
      filters,
      meta,
    ]
  );

  return (
    <BenefitsProviderContext.Provider value={contextValue}>
      {children}
    </BenefitsProviderContext.Provider>
  );
};
